import { cloneDeep } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

export const ANNOTATION_DRAG_TYPE = 'application/x-flow-annotation';
export const ANNOTATION_DEFAULT_WIDTH = 666;
export const ANNOTATION_DEFAULT_HEIGHT = 922;
export const ANNOTATION_MIN_WIDTH = 100;
export const ANNOTATION_MIN_HEIGHT = 100;
export const ANNOTATION_DEFAULT_BACKGROUND = '#f2f7ff';

const isFiniteNumber = value => Number.isFinite(value);

const isValidAnnotation = annotation => {
  const style = annotation?.style;

  return (
    annotation &&
    typeof annotation.id === 'string' &&
    annotation.id.length > 0 &&
    style &&
    isFiniteNumber(style.start_x) &&
    isFiniteNumber(style.start_y) &&
    isFiniteNumber(style.end_x) &&
    isFiniteNumber(style.end_y) &&
    style.end_x > style.start_x &&
    style.end_y > style.start_y
  );
};

export const normalizeAnnotations = value => {
  if (!Array.isArray(value)) return [];

  return value.filter(isValidAnnotation).map(annotation => {
    const normalized = cloneDeep(annotation);

    return {
      ...normalized,
      style: {
        ...normalized.style,
        background:
          typeof normalized.style.background === 'string' && normalized.style.background
            ? normalized.style.background
            : ANNOTATION_DEFAULT_BACKGROUND
      },
      content: typeof normalized.content === 'string' ? normalized.content : ''
    };
  });
};

export const createAnnotation = ({ x, y }, id = uuidv4()) => ({
  id,
  style: {
    start_x: x,
    start_y: y,
    end_x: x + ANNOTATION_DEFAULT_WIDTH,
    end_y: y + ANNOTATION_DEFAULT_HEIGHT,
    background: ANNOTATION_DEFAULT_BACKGROUND
  },
  content: ''
});

export const moveAnnotation = (annotation, { deltaX, deltaY }) => ({
  ...cloneDeep(annotation),
  style: {
    ...annotation.style,
    start_x: annotation.style.start_x + deltaX,
    start_y: annotation.style.start_y + deltaY,
    end_x: annotation.style.end_x + deltaX,
    end_y: annotation.style.end_y + deltaY
  }
});

export const resizeAnnotation = (annotation, direction, { deltaX, deltaY }) => {
  const resized = cloneDeep(annotation);
  const { start_x, start_y, end_x, end_y } = annotation.style;
  const resizeNorth = direction.includes('n');
  const resizeSouth = direction.includes('s');
  const resizeWest = direction.includes('w');
  const resizeEast = direction.includes('e');

  resized.style = {
    ...annotation.style,
    start_x: resizeWest ? Math.min(start_x + deltaX, end_x - ANNOTATION_MIN_WIDTH) : start_x,
    start_y: resizeNorth ? Math.min(start_y + deltaY, end_y - ANNOTATION_MIN_HEIGHT) : start_y,
    end_x: resizeEast ? Math.max(end_x + deltaX, start_x + ANNOTATION_MIN_WIDTH) : end_x,
    end_y: resizeSouth ? Math.max(end_y + deltaY, start_y + ANNOTATION_MIN_HEIGHT) : end_y
  };

  return resized;
};

export const resolveSnapshotAnnotations = (snapshot, fallback) =>
  Object.prototype.hasOwnProperty.call(snapshot ?? {}, 'annotations')
    ? normalizeAnnotations(snapshot.annotations)
    : normalizeAnnotations(fallback);
